import React, {useContext, useEffect, useState} from 'react';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {Form, Main_Loader, PageAlwaysVisible, PageDesktopPadding, PageTitle, ThemeContext, device} from 'monica-alexandria';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {Checkout} from '../../styles/Layout.css';
import {calculate_tax_manual} from '../../services/api/Subscriptions';
import i18n from '../../localization/i18next';
import Sidequest_Payment from './components/Sidequest_Payment';
import Sidequest_Product from './components/Sidequest_Product';
import Summary_Sidequest from './components/Summary_Sidequest';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK_TEST);

const CheckoutGrid = styled.div`
    display: grid;
    grid-template-columns: 30rem 1fr;
    grid-gap: 4rem;
    align-items: flex-start;

    @media ${device.xl} {
        grid-template-columns: 1fr;
    }
`;

const decode_session = session => {

    const parts = session?.split('.');
    if (!parts?.[1]) return false;

    let payloadText = parts[1].replaceAll('-', '+').replaceAll('_', '/');
    while (payloadText.length % 4) payloadText += '=';

    try {
        const payload = JSON.parse(window.atob(payloadText));
        if (!payload?.amount || parseInt(payload.amount) !== payload.amount || payload.amount < 50) return false;
        if (payload?.currency !== 'eur') return false;
        return payload;
    }
    catch(error) {
        console.log(error?.message || error?.msg);
        return false;
    }
};

export default function SIDEQUEST_CHECKOUT() {

    const {t} = useTranslation();
    const {theme} = useContext(ThemeContext);
    const location = useLocation();
    const [sessionInfo, setSessionInfo] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState('GR');
    const [invoice, setInvoice] = useState(null);
    const [taxError, setTaxError] = useState(false);
    const session = new URLSearchParams(location.search).get('session');

    useEffect(() => {
        setSessionInfo(decode_session(session));
    }, [session]);

    useEffect(() => {
        if (!sessionInfo) return;

        setInvoice(null);
        setTaxError(false);
        calculate_tax_manual(selectedCountry, false, sessionInfo.amount, true)
            .then(response => {
                if (response?.taxCalculation?.taxPercentage === undefined) {
                    setTaxError(true);
                    return;
                }

                const tax = Math.round(sessionInfo.amount * Number(response.taxCalculation.taxPercentage));
                setInvoice({tax, total: sessionInfo.amount + tax});
            })
            .catch(() => setTaxError(true));
    }, [selectedCountry, sessionInfo]);

    const elementsOptions = sessionInfo && invoice ? {
        mode: 'subscription',
        amount: invoice.total,
        currency: sessionInfo.currency,
        paymentMethodCreation: 'manual',
        locale: i18n.language || 'el',
        appearance: {
            theme: theme === 'dark' ? 'night' : 'stripe',
            labels: 'floating',
            variables: {
                fontSizeBase: '1.6',
                spacingUnit: '4',
                borderRadius: '1rem'
            }
        }
    } : null;

    return (
        <Checkout>
            <PageAlwaysVisible>
                <PageTitle>
                    <h1>{t('Checkout')}</h1>
                </PageTitle>
                {sessionInfo === null ? (
                    <Main_Loader></Main_Loader>
                ) : null}
                {sessionInfo === false ? (
                    <Form>
                        <h2>{t('Invalid payment session')}</h2>
                        <p>{t('This payment session is invalid. Request a new checkout link.')}</p>
                    </Form>
                ) : null}
                {sessionInfo && invoice ? (
                    <CheckoutGrid>
                        <Sidequest_Product></Sidequest_Product>
                        <Summary_Sidequest
                            amount={sessionInfo.amount}
                            tax={invoice.tax}
                            total={invoice.total}
                            currency={sessionInfo.currency}
                        ></Summary_Sidequest>
                    </CheckoutGrid>
                ) : null}
                {taxError ? (
                    <Form>
                        <p role="alert">{t('Could not calculate tax. Please try again.')}</p>
                    </Form>
                ) : null}
            </PageAlwaysVisible>
            <PageAlwaysVisible>
                <PageDesktopPadding>
                    {sessionInfo && !invoice && !taxError ? (
                        <Main_Loader></Main_Loader>
                    ) : null}
                    {elementsOptions ? (
                        <Form>
                            <h3>{t('Billing information')}</h3>
                            <Elements key={`${selectedCountry}-${invoice.total}`} stripe={stripePromise} options={elementsOptions}>
                                <Sidequest_Payment
                                    session={session}
                                    selectedCountry={selectedCountry}
                                    setSelectedCountry={setSelectedCountry}
                                ></Sidequest_Payment>
                            </Elements>
                        </Form>
                    ) : null}
                </PageDesktopPadding>
            </PageAlwaysVisible>
        </Checkout>
    );
}
